import React from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import {
  createEmptyTutorialUrl,
  normalizeTutorialUrlsForEditor
} from '../utils/tutorialUrls';

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
`;

const Meta = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #2563eb;
    background: #eff6ff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const HelperText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
`;

const EmptyState = styled.div`
  padding: 24px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #64748b;
  text-align: center;
  font-size: 14px;
`;

const LinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LinkRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: end;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #374151;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fff1f2;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #ffe4e6;
    border-color: #fda4af;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const TutorialUrlsEditor = ({
  value = [],
  onChange,
  disabled = false,
  itemNameSingular = 'tutorial URL',
  itemNamePlural = 'tutorial URLs',
  addButtonLabel = 'Add Tutorial URL',
  helperText = 'Add one or more tutorial URLs. Each one must be a full http:// or https:// link.',
  emptyStateText = 'No tutorial URLs added yet.',
  urlPlaceholder = 'https://example.com/tutorial'
}) => {
  const tutorialUrls = normalizeTutorialUrlsForEditor(value);

  const updateTutorialUrls = (nextTutorialUrls) => {
    onChange?.(nextTutorialUrls);
  };

  const handleAddTutorialUrl = () => {
    updateTutorialUrls([...tutorialUrls, createEmptyTutorialUrl()]);
  };

  const handleTutorialUrlChange = (index, fieldValue) => {
    updateTutorialUrls(
      tutorialUrls.map((tutorialUrl, tutorialUrlIndex) => (
        tutorialUrlIndex === index ? fieldValue : tutorialUrl
      ))
    );
  };

  const handleRemoveTutorialUrl = (index) => {
    updateTutorialUrls(
      tutorialUrls.filter((_, tutorialUrlIndex) => tutorialUrlIndex !== index)
    );
  };

  return (
    <Editor>
      <Toolbar>
        <Meta>
          {tutorialUrls.length} {tutorialUrls.length === 1 ? itemNameSingular : itemNamePlural}
        </Meta>
        <AddButton type="button" onClick={handleAddTutorialUrl} disabled={disabled}>
          <Plus size={16} />
          {addButtonLabel}
        </AddButton>
      </Toolbar>

      <HelperText>{helperText}</HelperText>

      {tutorialUrls.length ? (
        <LinksList>
          {tutorialUrls.map((tutorialUrl, index) => (
            <LinkRow key={`tutorial-url-${index}`}>
              <Field>
                <FieldLabel>URL</FieldLabel>
                <FieldInput
                  type="url"
                  value={tutorialUrl}
                  onChange={(event) => handleTutorialUrlChange(index, event.target.value)}
                  placeholder={urlPlaceholder}
                  disabled={disabled}
                />
              </Field>

              <RemoveButton
                type="button"
                onClick={() => handleRemoveTutorialUrl(index)}
                disabled={disabled}
                aria-label={`Remove ${itemNameSingular} ${index + 1}`}
              >
                <Trash2 size={16} />
              </RemoveButton>
            </LinkRow>
          ))}
        </LinksList>
      ) : (
        <EmptyState>{emptyStateText}</EmptyState>
      )}
    </Editor>
  );
};

export default TutorialUrlsEditor;
